import React, { useEffect, useRef, useState } from 'react'
import { announceToScreenReader, prefersReducedMotion, prefersHighContrast, generateId } from '@/lib/accessibility'

// Hook for managing focus
export const useFocus = () => {
    const ref = useRef<HTMLElement>(null)

    const focus = () => {
        ref.current?.focus()
    }

    return [ref, focus] as const
}

// Hook for generating stable IDs
export const useId = (prefix?: string) => {
    const [id] = useState(() => generateId(prefix))
    return id
}

// Hook for screen reader announcements
export const useAnnouncer = () => {
    const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
        announceToScreenReader(message, priority)
    }

    return announce
}

// Hook for detecting accessibility preferences
export const useAccessibilityPreferences = () => {
    const [reducedMotion, setReducedMotion] = useState(false)
    const [highContrast, setHighContrast] = useState(false)

    useEffect(() => {
        // Initial values
        setReducedMotion(prefersReducedMotion())
        setHighContrast(prefersHighContrast())

        // Listen for changes
        const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        const highContrastQuery = window.matchMedia('(prefers-contrast: high)')

        const handleReducedMotionChange = (e: MediaQueryListEvent) => {
            setReducedMotion(e.matches)
        }

        const handleHighContrastChange = (e: MediaQueryListEvent) => {
            setHighContrast(e.matches)
        }

        reducedMotionQuery.addEventListener('change', handleReducedMotionChange)
        highContrastQuery.addEventListener('change', handleHighContrastChange)

        return () => {
            reducedMotionQuery.removeEventListener('change', handleReducedMotionChange)
            highContrastQuery.removeEventListener('change', handleHighContrastChange)
        }
    }, [])

    return { reducedMotion, highContrast }
}

// Hook for keyboard navigation
export const useKeyboardNavigation = (
    items: HTMLElement[],
    orientation: 'horizontal' | 'vertical' = 'horizontal'
) => {
    const [currentIndex, setCurrentIndex] = useState(0)

    const handleKeyDown = (e: KeyboardEvent) => {
        const { key } = e
        let newIndex = currentIndex

        if (orientation === 'horizontal') {
            if (key === 'ArrowLeft') {
                newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
            } else if (key === 'ArrowRight') {
                newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
            }
        } else {
            if (key === 'ArrowUp') {
                newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
            } else if (key === 'ArrowDown') {
                newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
            }
        }

        if (key === 'Home') {
            newIndex = 0
        } else if (key === 'End') {
            newIndex = items.length - 1
        }

        if (newIndex !== currentIndex) {
            e.preventDefault()
            setCurrentIndex(newIndex)
            items[newIndex]?.focus()
        }
    }

    return { currentIndex, setCurrentIndex, handleKeyDown }
}

// Hook for managing ARIA live regions
export const useLiveRegion = () => {
    const [message, setMessage] = useState('')
    const [priority, setPriority] = useState<'polite' | 'assertive'>('polite')

    const announce = (newMessage: string, newPriority: 'polite' | 'assertive' = 'polite') => {
        setMessage(newMessage)
        setPriority(newPriority)

        // Clear message after announcement
        setTimeout(() => setMessage(''), 1000)
    }

    const LiveRegion = () => React.createElement('div', {
        'aria-live': priority,
        'aria-atomic': 'true',
        className: 'sr-only'
    }, message)

    return { announce, LiveRegion }
}

// Hook for focus trap
export const useFocusTrap = (isActive: boolean) => {
    const containerRef = useRef<HTMLElement>(null)

    useEffect(() => {
        if (!isActive || !containerRef.current) return

        const container = containerRef.current
        const focusableElements = container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        const handleTabKey = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    lastElement.focus()
                    e.preventDefault()
                }
            } else {
                if (document.activeElement === lastElement) {
                    firstElement.focus()
                    e.preventDefault()
                }
            }
        }

        container.addEventListener('keydown', handleTabKey)

        // Focus first element when trap becomes active
        firstElement?.focus()

        return () => {
            container.removeEventListener('keydown', handleTabKey)
        }
    }, [isActive])

    return containerRef
}

// Hook for managing skip links
export const useSkipLinks = () => {
    const skipLinksRef = useRef<HTMLDivElement>(null)

    const addSkipLink = (href: string, text: string) => {
        if (!skipLinksRef.current) return

        const link = document.createElement('a')
        link.href = href
        link.textContent = text
        link.className = 'skip-link'
        link.addEventListener('click', (e) => {
            e.preventDefault()
            const target = document.querySelector(href) as HTMLElement
            if (target) {
                target.focus()
                target.scrollIntoView()
            }
        })

        skipLinksRef.current.appendChild(link)
    }

    const SkipLinks = () => React.createElement('div', {
        ref: skipLinksRef,
        className: 'skip-links'
    })

    return { addSkipLink, SkipLinks }
}